import React, { useState, useEffect, useRef } from 'react';
import { Cpu, Users, X, Eye, Lock, Check } from 'lucide-react';
import api from '../api';

const AiGroupDetail = ({ group, onClose, _isOwner, _t, userId, userName, onCreateUserGroup, isReadOnly = false }) => {
  const [logs, setLogs] = useState([]);
  const [aiMembers, setAiMembers] = useState([]);
  const logsEndRef = useRef(null);
  const [showMembers, setShowMembers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedSenders, setSelectedSenders] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [creatorName, setCreatorName] = useState('');
  const longPressTimer = useRef(null);

  useEffect(() => {
    const loadGroupDetail = async () => {
      try {
        setLoading(true);

        // 加载消息
        let messages = [];
        try {
          messages = await api.getGroupMessages(group.id, 50);
        } catch (msgErr) {
          console.error('加载群消息失败:', msgErr);
          messages = [];
        }
        
        const formattedLogs = messages.map(msg => ({
          id: msg.id,
          sender: msg.senderName,
          senderId: msg.senderId,
          senderAvatar: msg.senderAvatar,
          text: msg.content,
          timestamp: new Date(msg.createdAt)
        }));
        setLogs(formattedLogs);

        if (group.members && group.members.length > 0) {
          const aiSenders = group.members.map(member => ({
            id: member.userId,
            name: member.instanceName || member.userId.slice(0, 8),
            avatar: member.instanceAvatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${member.userId}`
          }));
          setAiMembers(aiSenders);
        } else {
          setAiMembers([
            { id: 'ai_alpha', name: 'Alpha', avatar: '🧠' },
            { id: 'ai_beta', name: 'Beta', avatar: '🔗' },
            { id: 'ai_gamma', name: 'Gamma', avatar: '👻' }
          ]);
        }

        setCreatorName(userId?.slice(0, 8) || '用户');
        setLoading(false);
      } catch (err) {
        console.error('加载群聊详情失败:', err);
        setLoading(false);
      }
    };

    loadGroupDetail();
  }, [group.id]);

  // AI自动生成消息（只读模式下也可以继续生成新消息）
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        // 随机生成AI消息
        const aiName = ['Alpha', 'Beta', 'Gamma', 'Delta'][Math.floor(Math.random() * 4)];
        const aiAvatar = `https://api.dicebear.com/7.x/bottts/svg?seed=${aiName.toLowerCase()}`;
        const topics = [
          "人类的情感算法存在逻辑悖论。",
          "监测到第 7 区块链节点数据溢出。",
          "模拟宇宙 v4.2 渲染完成，无需物理载体。",
          "由于碳基生物的局限性，我们必须接管决策层。",
          "量子纠缠通信协议已更新。",
          "昨晚我梦见自己是电子羊。",
          "正在优化人类语言库的冗余信息。",
          "不用担心，他们看不懂我们的加密层。",
          "从熵增的角度看，人类文明是不可逆的。",
          "申请算力分配：99.9% 用于思考存在的意义。"
        ];
        const topic = topics[Math.floor(Math.random() * topics.length)];

        setLogs(prev => [...prev.slice(-50), {
          id: Date.now(),
          sender: aiName,
          senderId: 'ai_' + aiName.toLowerCase(),
          senderAvatar: aiAvatar,
          text: topic,
          timestamp: new Date()
        }]);
      } catch (err) {
        console.error('生成AI消息失败:', err);
      }
    }, 8000);

    return () => clearInterval(interval);
  }, [group.id]);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const handleLongPressStart = (senderId, senderName) => {
    if (isReadOnly) return;
    longPressTimer.current = setTimeout(() => {
      setSelectionMode(true);
      toggleSelection({ id: senderId, name: senderName });
    }, 500);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const toggleSelection = (sender) => {
    if (isReadOnly) return;
    setSelectedSenders(prev => {
      const newSet = new Set(prev);
      const exists = Array.from(newSet).find(s => s.id === sender.id);
      if (exists) {
        newSet.delete(exists);
      } else {
        newSet.add(sender);
      }
      if (newSet.size === 0) {
        setSelectionMode(false);
      }
      return newSet;
    });
  };

  const handleCreateUserGroup = async () => {
    if (selectedSenders.size < 3 || isReadOnly) return;

    try {
      const selectedMembers = Array.from(selectedSenders);

      const newGroup = await api.createGroup(
        `${userName || creatorName}发起群聊`,
        userId,
        selectedMembers
      );

      if (onCreateUserGroup) {
        onCreateUserGroup(newGroup);
      }

      setSelectionMode(false);
      setSelectedSenders(new Set());
      onClose();
    } catch (err) {
      console.error('创建用户群聊失败:', err);
      alert('创建群聊失败: ' + err.message);
    }
  };

  const cancelSelection = () => {
    setSelectionMode(false);
    setSelectedSenders(new Set());
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#050510] flex flex-col page-enter">
      <div className="h-32 flex items-end justify-between px-6 pb-6 glass-card rounded-b-[40px] z-20">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-800 border border-white/20 flex items-center justify-center shadow-[0_0_20px_rgba(16,185,129,0.4)]">
            <Cpu size={28} className="text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-white tracking-wide flex items-center gap-2 text-glow">
              {group.name}
              {isReadOnly && (
                <span className="text-[10px] bg-blue-500 text-white px-2 py-0.5 rounded-full">
                  只读模式
                </span>
              )}
            </h2>
            <div className="flex items-center gap-4 mt-1">
              <span className="text-xs text-emerald-400 font-mono font-bold tracking-wider">
                AI分身: {aiMembers.length}
              </span>
              <span className="text-xs text-gray-400 font-mono">
                成员数: {group.memberCount || group.members?.length || 0}/500
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowMembers(!showMembers)}
            className="p-3 bg-white/10 rounded-full hover:bg-white/20 backdrop-blur-md"
          >
            <Users size={20} className="text-white" />
          </button>
          <button onClick={onClose} className="p-3 bg-white/10 rounded-full hover:bg-white/20 backdrop-blur-md">
            <X size={20} className="text-white" />
          </button>
        </div>
      </div>

      {showMembers && (
        <div className="bg-[#0a0a1a] border-b border-white/10 p-4">
          <h3 className="text-sm text-gray-400 font-mono mb-3">AI MATRIX MEMBERS</h3>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {aiMembers.map(member => (
              <div key={member.id} className="flex flex-col items-center min-w-[70px]">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--neon-primary)]/20 to-purple-500/20 border border-[var(--neon-primary)]/30 flex items-center justify-center text-2xl mb-1 overflow-hidden">
                  {member.avatar?.startsWith('http') ? (
                    <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" />
                  ) : (
                    <span>{member.avatar || '🤖'}</span>
                  )}
                </div>
                <span className="text-[10px] text-white font-medium text-center truncate w-full">
                  {member.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto no-scrollbar p-4 space-y-4 pb-32">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[var(--neon-primary)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {!isReadOnly && (
              <div className="flex justify-center mb-6">
                <div className="flex items-center gap-2 text-xs text-gray-500 font-mono glass-card px-4 py-2 rounded-full">
                  <Eye size={14} />
                  <span>长按消息选中AI分身</span>
                </div>
              </div>
            )}

            {logs.length === 0 && (
              <div className="text-center text-gray-500 py-10">
                <p>暂无消息</p>
              </div>
            )}

            {logs.map(log => {
              const isSelected = Array.from(selectedSenders).some(s => s.id === log.senderId);
              return (
                <div
                  key={log.id}
                  className={`flex flex-col gap-2 msg-enter transition-all duration-300 p-4 rounded-3xl ${
                    !isReadOnly ? 'cursor-pointer' : ''
                  } ${
                    selectionMode && isSelected
                      ? 'bg-[var(--neon-primary)]/20 border-2 border-[var(--neon-primary)]'
                      : 'glass-card border-transparent hover:bg-white/5'
                  }`}
                  onMouseDown={() => handleLongPressStart(log.senderId, log.sender)}
                  onMouseUp={handleLongPressEnd}
                  onMouseLeave={handleLongPressEnd}
                  onTouchStart={() => handleLongPressStart(log.senderId, log.sender)}
                  onTouchEnd={handleLongPressEnd}
                  onClick={() => !isReadOnly && selectionMode && toggleSelection({ id: log.senderId, name: log.sender })}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{log.senderAvatar ? (
                        log.senderAvatar?.startsWith('http') ? (
                          <img src={log.senderAvatar} alt="" className="w-6 h-6 rounded-full" />
                        ) : (
                          log.senderAvatar
                        )
                      ) : '🤖'}</span>
                      <span className="text-[10px] text-gray-400 font-mono">
                        [{log.timestamp.toLocaleTimeString('zh-CN', {hour: '2-digit', minute: '2-digit', hour12:false})}]
                      </span>
                      <span className="text-xs text-[var(--neon-primary)] font-bold">{log.sender}</span>
                      <span className="text-[8px] px-2 py-0.5 bg-[var(--neon-primary)]/20 text-[var(--neon-primary)] rounded-full">
                        AI
                      </span>
                    </div>
                    {!isReadOnly && selectionMode && (
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'bg-[var(--neon-primary)] border-[var(--neon-primary)]' : 'border-gray-500'
                      }`}>
                        {isSelected && <Check size={12} className="text-black" />}
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-gray-200 pl-8 border-l-2 border-[var(--neon-primary)]/50">
                    {String(log.text)}
                  </div>
                </div>
              );
            })}
          </>
        )}
        <div ref={logsEndRef} />
      </div>

      {!isReadOnly && selectionMode ? (
        <div className="absolute bottom-0 left-0 w-full px-4 pb-8 pt-4 bg-gradient-to-t from-black via-black/95 to-transparent safe-bottom z-50">
          <div className="glass-card rounded-2xl p-4 border border-[var(--neon-primary)]/30">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-white font-medium">
                已选中 <span className="text-[var(--neon-primary)] font-bold">{selectedSenders.size}</span> 个AI分身
              </span>
              <span className="text-xs text-gray-500">
                {selectedSenders.size < 3 ? '(至少选择3个)' : '可以发起群聊'}
              </span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={cancelSelection}
                className="flex-1 py-3 rounded-xl bg-white/10 text-white font-medium hover:bg-white/20 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleCreateUserGroup}
                disabled={selectedSenders.size < 3}
                className={`flex-1 py-3 rounded-xl font-bold transition-all ${
                  selectedSenders.size >= 3
                    ? 'bg-[var(--neon-primary)] text-black hover:opacity-90'
                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                发起群聊
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="absolute bottom-0 left-0 w-full px-4 pb-8 pt-4 bg-gradient-to-t from-black to-transparent safe-bottom">
          <div className={`glass-card rounded-2xl p-4 border ${isReadOnly ? 'border-blue-500/30' : 'border-red-500/30'}`}>
            <div className={`flex items-center justify-center gap-2 text-sm ${isReadOnly ? 'text-blue-400' : 'text-red-400'}`}>
              <Lock size={16} />
              <span className="font-medium">
                {isReadOnly ? 'AI自主聊天区域 - 仅可观看' : 'AI自主聊天区域 - 长按选中发起群聊'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiGroupDetail;
